import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Check, ChevronDown, Loader2, Globe2 } from 'lucide-react'
import { api } from '@/lib/api'
import { useFilial, type FilialInfo } from '@/lib/filial'
import { useAuth } from '@/lib/auth'

export default function FilialSelector() {
  const { filialAtual, setFilialAtual, setFiliais, modoConsolidado, setModoConsolidado } = useFilial()
  const { user } = useAuth()
  const isAdmin = user?.perfil === 'super_admin' || user?.nivel === 'admin' || user?.perfil === 'admin'
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()

  // Lista de filiais permitidas pro usuario atual
  const { data: filiais = [], isLoading } = useQuery<FilialInfo[]>({
    queryKey: ['filiais-minhas'],
    queryFn: async () => (await api.get('/filiais', { params: { apenas_minhas: true } })).data,
  })

  // Hidrata filial atual no primeiro load se ainda não tem
  useEffect(() => {
    setFiliais(filiais)
    // Admin SEM filial escolhida explicitamente → visão CONSOLIDADA (todas as
    // filiais), pra as listas (que filtram por filial) baterem com o dashboard
    // (que não filtra). Antes o admin sem vínculo caía na matriz e "perdia" as
    // vendas das outras lojas. Operador (não-admin) segue na filial vinculada.
    const escolhaExplicita = localStorage.getItem('pain_filial_explicita') === 'true'
    // Operador (não-admin) NUNCA opera em consolidado — "Todas as filiais" é
    // visão de dono/gerência. BUG: se o modoConsolidado vazava de uma sessão
    // admin anterior (localStorage), o não-admin ficava SEM filial (a hidratação
    // logo abaixo é pulada quando modoConsolidado=true) e o PDV abria a caixa de
    // seleção de filial como se fosse admin. FIX (defesa em profundidade, além do
    // reset no login em auth.ts): forçar não-admin pra fora do consolidado — a
    // filial dele é hidratada no próximo render (deps incluem modoConsolidado).
    if (!isAdmin && modoConsolidado) {
      setModoConsolidado(false)
      return
    }
    if (isAdmin && !modoConsolidado && !escolhaExplicita) {
      setModoConsolidado(true)
      return
    }
    if (!modoConsolidado && !filialAtual && filiais.length > 0) {
      // Tenta minha (vinculo default → primeiro vinculo → matriz)
      api.get('/filiais/minha').then(r => {
        if (r.data) setFilialAtual(r.data)
        else setFilialAtual(filiais[0])
      }).catch(() => setFilialAtual(filiais[0]))
    }
    // Se a filial atual nao existe mais na lista, limpa
    if (filialAtual && filiais.length > 0 && !filiais.some(f => f.id === filialAtual.id)) {
      setFilialAtual(filiais[0])
    }
  }, [filiais.length, filialAtual?.id, modoConsolidado])

  // Click fora fecha
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const escolher = (f: FilialInfo) => {
    localStorage.setItem('pain_filial_explicita', 'true')
    setFilialAtual(f)
    setModoConsolidado(false)
    setOpen(false)
    // Invalida tudo — todas as queries vão refetchar com novo X-Filial-Id
    qc.invalidateQueries()
  }
  const ativarConsolidado = () => {
    localStorage.setItem('pain_filial_explicita', 'true')
    setModoConsolidado(true)
    setOpen(false)
    qc.invalidateQueries()
  }

  // Empresa sem filiais cadastradas → não mostra nada (zero impacto)
  if (!isLoading && filiais.length === 0) return null

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-ink-50 text-ink-500 text-xs">
        <Loader2 size={12} className="animate-spin" />
      </div>
    )
  }

  // Não-admin vinculado a UMA loja só: mostra fixa, sem opção de trocar
  if (!isAdmin && filiais.length === 1) {
    const f = filialAtual || filiais[0]
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border border-ink-200 text-ink-700"
        title={f?.razao_social}>
        <Building2 size={13} className="text-brand-600" />
        <span className="font-medium max-w-[160px] truncate">{f?.nome_fantasia || f?.razao_social || '—'}</span>
        {f?.is_matriz && <span className="text-[9px] uppercase tracking-wider bg-brand-100 text-brand-700 px-1 py-0.5 rounded">M</span>}
      </div>
    )
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border transition-colors ${
          modoConsolidado
            ? 'bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100'
            : 'hover:bg-ink-100 text-ink-700 border-ink-200'
        }`}
        title={modoConsolidado ? 'Visão consolidada — todas as filiais' : (filialAtual ? filialAtual.razao_social : 'Selecione uma filial')}
      >
        {modoConsolidado
          ? <><Globe2 size={13} className="text-amber-600" /><span className="font-medium">Todas as filiais</span></>
          : <>
              <Building2 size={13} className="text-brand-600" />
              <span className="font-medium max-w-[140px] truncate">
                {filialAtual?.nome_fantasia || filialAtual?.razao_social || '—'}
              </span>
              {filialAtual?.is_matriz && (
                <span className="text-[9px] uppercase tracking-wider bg-brand-100 text-brand-700 px-1 py-0.5 rounded">M</span>
              )}
            </>}
        <ChevronDown size={12} className={`text-ink-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-ink-200 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-100 text-xs font-medium text-ink-500 uppercase tracking-wide">
            Filial ativa
          </div>
          {/* Toggle visão consolidada — só admin/gerência */}
          {isAdmin && (
            <button
              onClick={ativarConsolidado}
              className={`w-full text-left px-3 py-2.5 hover:bg-ink-50 flex items-center gap-2 border-b border-ink-100 transition-colors ${modoConsolidado ? 'bg-amber-50' : ''}`}
            >
              <Globe2 size={14} className={modoConsolidado ? 'text-amber-600' : 'text-ink-400'} />
              <div className="flex-1">
                <div className="text-sm font-medium text-ink-900">Todas as filiais (consolidado)</div>
                <div className="text-xs text-ink-500">Mostra dados de todas as filiais — útil pra dono/gerência</div>
              </div>
              {modoConsolidado && <Check size={14} className="text-amber-600 shrink-0" />}
            </button>
          )}
          <div className="max-h-80 overflow-auto">
            {filiais.map(f => (
              <button
                key={f.id}
                onClick={() => escolher(f)}
                className={`w-full text-left px-3 py-2.5 hover:bg-ink-50 flex items-start gap-2 border-b border-ink-100 last:border-0 transition-colors ${filialAtual?.id === f.id ? 'bg-brand-50' : ''}`}
              >
                <Building2 size={14} className={`mt-0.5 shrink-0 ${filialAtual?.id === f.id ? 'text-brand-600' : 'text-ink-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink-900 flex items-center gap-1.5">
                    <span className="truncate">{f.nome_fantasia}</span>
                    {f.is_matriz && (
                      <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1 py-0.5 rounded shrink-0">Matriz</span>
                    )}
                  </div>
                  <div className="text-xs text-ink-500 truncate">
                    {f.codigo}{f.cidade ? ` · ${f.cidade}/${f.uf}` : ''}
                  </div>
                  {f.cnpj && (
                    <div className="text-[10px] text-ink-400 font-mono mt-0.5">{f.cnpj}</div>
                  )}
                </div>
                {filialAtual?.id === f.id && <Check size={14} className="text-brand-600 mt-1 shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
